<template>
  <div class="asc-plot-box-chart">
    <template v-if="!isEmpty">
      <template v-if="!disabled && !miniPlot">
        <div class="asc-plot-box-chart--sort-button">
          <asc-sort-box-chart-button
            :sort="sortItem"
            :on-sort="handleSort"
            :disabled="disabled"
          ></asc-sort-box-chart-button>
        </div>
      </template>
      <asc-box-chart
        :options="getUpdatedChartOptions"
        :full-size="isFullSizeNoData"
      ></asc-box-chart>
      <template v-if="hasMissingTails">
        <div class="asc-plot-box-chart--missing-tails">
          <asc-label size="sm" :value="getMissingTailsData"></asc-label>
        </div>
      </template>
    </template>
    <asc-no-data v-if="isEmpty"></asc-no-data>
  </div>
</template>

<script>
import AscBoxChart from './AscBoxChart'
import AscSortBoxChartButton from '@/components/atoms/AscSortBoxChartButton'
import AscNoData from '@/components/atoms/AscNoData'
import AscLabel from '@/components/atoms/AscLabel'
import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscPlotBoxChart',

  components: {
    AscBoxChart,
    AscSortBoxChartButton,
    AscNoData,
    AscLabel,
  },

  props: {
    pagePlot: {
      type: Object,
      default: () => {},
    },
    chartIndex: {
      type: Number,
      required: true,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    unit: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    miniPlot: {
      type: Boolean,
      default: false,
    },
    mxRecPlot: {
      type: Boolean,
      default: true,
    },
    reportsPlot: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currentPagePlot: Object.assign({}, this.pagePlot),
      chartOptions: null,
      sortItem: {
        sortField: 'pct50',
        sortFieldName: 'Med',
        sortAsc: false,
      },
      defaultOptions: {
        chart: {
          marginRight: 40,
          type: 'boxplot',
          inverted: true,
        },
        legend: {
          enabled: false,
        },
        title: {
          text: '',
        },
        xAxis: {
          title: {
            text: '',
          },
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
        series: [],
      },
    }
  },

  computed: {
    isFullSizeNoData() {
      return this.mxRecPlot || this.reportsPlot
    },

    getUpdatedChartOptions() {
      if (this.chartOptions) {
        return this.chartOptions
      }
      this.updateChartOptions()
      return this.chartOptions
    },

    getSortItem() {
      return this.sortItem
    },

    isEmpty() {
      const validData = this.getChartData !== null
      if (!validData) return true
      const data = this.getChartData
      return (
        (data.fleets === null || data.fleets.length === 0) &&
        (data.tails === null || data.tails.length === 0)
      )
    },

    hasMissingTails() {
      const missingTails = this.getChartMissingTails
      return missingTails.length > 0
    },

    getMissingTailsData() {
      const missingTails = this.getChartMissingTails
      return `Missing Tails: ${missingTails.join(', ')}`
    },

    getChartMissingTails() {
      if (this.currentPagePlot && this.chartData) {
        const [first] = this.chartData.filter(
          item =>
            item.plotId === this.currentPagePlot.id &&
            item.chartIndex === this.chartIndex,
        )
        return (first && first.missingTails) || []
      }
      return []
    },

    getChartData() {
      if (this.currentPagePlot && this.chartData) {
        const [first] = this.chartData.filter(
          item =>
            item.plotId === this.currentPagePlot.id &&
            item.chartIndex === this.chartIndex,
        )
        return (first && first.data) || null
      }
      return null
    },

    getChartOptions() {
      const chart = this.buildChart
      const series = this.buildSeries
      const xAxis = this.buildXAxis
      const yAxis = this.buildYAxis

      return {
        ...this.defaultOptions,
        ...chart,
        ...xAxis,
        ...yAxis,
        ...series,
      }
    },

    buildChart() {
      const data = this.getMappedChartData()
      const height = data.length * 40 + 50

      return {
        chart: {
          ...this.defaultOptions.chart,
          height: height < 500 ? 500 : height,
        },
      }
    },

    buildXAxis() {
      const data = this.getMappedChartData()
      const categories = data.map(item => {
        return item.name.toUpperCase()
      })
      return {
        xAxis: {
          ...this.defaultOptions.xAxis,
          categories,
        },
      }
    },

    buildYAxis() {
      return {
        yAxis: {
          ...this.defaultOptions.yAxis,
          opposite: true,
          labels: {
            formatter: function () {
              return Number.isInteger(this.value)
                ? this.value
                : this.value.toFixed(2)
            },
          },
        },
      }
    },

    buildSeries() {
      const data = this.getMappedChartData()
      const _that = this
      const series = data.map(item => {
        return {
          name: item.name,
          pointWidth: 14,
          medianWidth: 4,
          whiskerLength: 12,
          whiskerWidth: 4,
          data: item.data,
          color:
            this.getChartData.tails &&
            this.getChartData.tails.some(
              t => t.tail && t.tail.toLowerCase() === item.name?.toLowerCase(),
            )
              ? '#2D6BFF'
              : variables.accent500,
          tooltip: {
            headerFormat: '<b>{point.key}</b><br/>',
            valueDecimals: 2,
            pointFormatter: function () {
              return (
                'Maximum: ' +
                this.high.toFixed(2) +
                ' ' +
                _that.unit +
                '<br/>' +
                'Upper quartile: ' +
                this.q3.toFixed(2) +
                ' ' +
                _that.unit +
                '<br/>' +
                'Median: ' +
                this.median.toFixed(2) +
                ' ' +
                _that.unit +
                '<br/>' +
                'Lower quartile: ' +
                this.q1.toFixed(2) +
                ' ' +
                _that.unit +
                '<br/>' +
                'Minimum: ' +
                this.low.toFixed(2) +
                ' ' +
                _that.unit +
                '<br/>'
              )
            },
          },
        }
      })

      return {
        series: series,
      }
    },
  },

  watch: {
    pagePlot: {
      deep: true,
      handler(newPagePlot) {
        if (newPagePlot.dataUpdated !== this.currentPagePlot.dataUpdated) {
          this.currentPagePlot = Object.assign({}, newPagePlot)
          this.pagePlot = newPagePlot
          this.dataTabs = newPagePlot.options.data.tabs
          this.updateChartOptions(this.getChartOptions)
        }
      },
    },
  },

  methods: {
    updateChartOptions() {
      this.chartOptions = this.getChartOptions
    },

    getMappedChartData() {
      const validData = this.getChartData !== null
      if (!validData) return []

      const mapQuartileObject = quartileObject => {
        const { index, min, pct25, pct50, pct75, max } = quartileObject
        return [index, min, pct25, pct50, pct75, max]
      }

      let fleetValues = []
      if (this.getChartData.fleets) {
        fleetValues = this.getChartData.fleets
          .sort((a, b) => (a.tail > b.tail ? -1 : 1))
          .sort((a, b) =>
            a[this.sortItem.sortField] > b[this.sortItem.sortField]
              ? this.sortItem.sortAsc
                ? 1
                : -1
              : this.sortItem.sortAsc
              ? -1
              : 1,
          )
          .map((item, index) => {
            const updatedItem = {
              index,
              ...item,
            }
            return {
              name: item.tail,
              data: [mapQuartileObject(updatedItem)],
            }
          })
      }

      let tailValues = []
      if (this.getChartData.tails) {
        tailValues = this.getChartData.tails
          .sort((a, b) => (a.tail > b.tail ? -1 : 1))
          .sort((a, b) =>
            a[this.sortItem.sortField] > b[this.sortItem.sortField]
              ? this.sortItem.sortAsc
                ? 1
                : -1
              : this.sortItem.sortAsc
              ? -1
              : 1,
          )
          .map((item, index) => {
            const updatedIndex = fleetValues.length + index
            const updatedItem = {
              index: updatedIndex,
              ...item,
            }
            return {
              name: item.tail,
              data: [mapQuartileObject(updatedItem)],
            }
          })
      }

      return [...fleetValues, ...tailValues]
    },

    handleSort(sortItem) {
      this.sortItem = sortItem
      this.updateChartOptions(this.getChartOptions)
    },
  },
}
</script>

<style lang="scss">
.asc-plot-box-chart {
  margin-bottom: 16px;
  max-height: 390px;
  overflow-y: auto;

  &--sort-button {
    padding-top: 8px;
    padding-right: 8px;
    margin-bottom: 8px;
  }

  &--missing-tails {
    margin: 16px;
  }
}
</style>
